import React, { useEffect, useState } from "react";
import { getBarGraphData, getSecondBarGraphData } from "../../api/Api";
import { Bar, Line } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BarGraph = () => {
    const [barGraphData, setBarGraphData] = useState([]);
    const [subscriberGraphData, setSubscriberGraphData] = useState([]);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [chartType, setChartType] = useState('bar');

    // Function to get minimum allowed date (April 18th, 2025)
    const getMinAllowedDate = () => {
        return '2025-04-18';
    };

    // Function to get today's date
    const getToday = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Function to check if week selection is available
    const isWeekSelectionAvailable = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 7;
    };

    // Function to check if month selection is available
    const isMonthSelectionAvailable = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 30;
    };

    const [filters, setFilters] = useState({
        flag: "Day",
        startDate: getMinAllowedDate(),
        endDate: getToday()
    });

    const getPayloadFlag = (selectedFlag, startDate, endDate) => {
        if (selectedFlag === "Day") {
            return "Date";
        }
        if (selectedFlag === "Week") {
            return "Date";
        }
        if (selectedFlag === "Month") {
            return "Month";
        }
        return selectedFlag;
    };

    const adjustDatesBasedOnFlag = (flag) => {
        const today = new Date();
        let startDate, endDate;

        switch (flag) {
            case "Day":
                startDate = getMinAllowedDate();
                endDate = startDate;
                break;
            case "Week":
                if (isWeekSelectionAvailable(getMinAllowedDate(), getToday())) {
                    startDate = new Date(today);
                    startDate.setDate(today.getDate() - 6);
                    startDate = startDate.toISOString().split('T')[0];
                    endDate = today.toISOString().split('T')[0];
                } else {
                    startDate = getMinAllowedDate();
                    endDate = startDate;
                }
                break;
            case "Month":
                if (isMonthSelectionAvailable(getMinAllowedDate(), getToday())) {
                    startDate = new Date(today);
                    startDate.setMonth(today.getMonth() - 1);
                    startDate = startDate.toISOString().split('T')[0];
                    endDate = today.toISOString().split('T')[0];
                } else {
                    startDate = getMinAllowedDate();
                    endDate = startDate;
                }
                break;
            default:
                startDate = getMinAllowedDate();
                endDate = startDate;
                break;
        }

        return { startDate, endDate };
    };

    const fetchBarGraphData = async (filterParams) => {
        try {
            setIsFilterLoading(true);
            const payload = {
                ...filterParams,
                flag: getPayloadFlag(filterParams.flag, filterParams.startDate, filterParams.endDate)
            };
            const response = await getBarGraphData(payload);
            if (response?.statusDescription?.status === 200) {
                setBarGraphData({
                    data: response.cardsResponses || [],
                    labels: response.xaxisLabel || []
                });
            }
        } catch (error) {
            console.error('Error fetching graph data:', error);
        } finally {
            setIsFilterLoading(false);
        }
    };

    const fetchSecondBarGraphData = async (filterParams) => {
        try {
            setIsFilterLoading(true);
            const payload = {
                ...filterParams,
                flag: getPayloadFlag(filterParams.flag, filterParams.startDate, filterParams.endDate)
            };
            const response = await getSecondBarGraphData(payload);
            if (response?.statusDescription?.status === 200) {
                setSubscriberGraphData({
                    data: response.cardsResponses || [],
                    labels: response.xaxisLabel || []
                });
            }
        } catch (error) {
            console.error('Error fetching subscriber graph data:', error);
        } finally {
            setIsFilterLoading(false);
        }
    };

    useEffect(() => {
        fetchBarGraphData(filters);
        fetchSecondBarGraphData(filters);
    }, [filters]);

    const handleFilterChange = (name, value) => {
        if (name === 'flag') {
            const newDates = adjustDatesBasedOnFlag(value);
            const newFilters = {
                ...filters,
                flag: value,
                startDate: newDates.startDate,
                endDate: newDates.endDate
            };
            setFilters(newFilters);
        } else {
            // Validate dates when either start or end date changes
            if (name === 'startDate') {
                const minDate = new Date(getMinAllowedDate());
                const selectedDate = new Date(value);
                if (selectedDate < minDate) {
                    value = getMinAllowedDate();
                }
                if (value > filters.endDate) {
                    const newFilters = { ...filters, startDate: value, endDate: value };
                    setFilters(newFilters);
                } else {
                    const newFilters = { ...filters, [name]: value };
                    setFilters(newFilters);
                }
            } else if (name === 'endDate') {
                if (value < filters.startDate) {
                    const newFilters = { ...filters, startDate: value, endDate: value };
                    setFilters(newFilters);
                } else {
                    const newFilters = { ...filters, [name]: value };
                    setFilters(newFilters);
                }
            }
        }
    };

    const processChartData = (response) => {
        if (!response?.data || !Array.isArray(response.data)) return null;

        return {
            labels: response.labels || [],
            datasets: response.data.map(item => ({
                label: item.title,
                data: item.data,
                backgroundColor: item.colour === 'text-success' ? '#28a745' : 
                               item.colour === 'text-warning' ? '#ffc107' : 
                               item.colour,
                borderColor: item.colour === 'text-success' ? '#28a745' : 
                           item.colour === 'text-warning' ? '#ffc107' : 
                           item.colour,
            }))
        };
    };

    const processSubscriberChartData = () => {
        if (!subscriberGraphData?.data || !Array.isArray(subscriberGraphData.data)) return null;

        return {
            labels: subscriberGraphData.labels,
            datasets: subscriberGraphData.data.map(item => ({
                label: item.title,
                data: item.data,
                backgroundColor: item.colour === 'text-success' ? '#28a745' : '#ffc107',
                borderColor: item.colour === 'text-success' ? '#28a745' : '#ffc107',
            }))
        };
    };

    const chartData = processChartData(barGraphData);
    const subscriberData = processSubscriberChartData();
    const ChartComponent = chartType === 'bar' ? Bar : Line;

    return (
        <>
        <div className="contop_card card my-4 p-md-4 p-3">
            <div className="pb-4">
                <div className="">
                    <div className="">
                        <div className="d-flex justify-content-between flex-column flex-md-row">
                            <div className="d-flex gap-2">
                                <div className="form-group position-relative w-50">
                                <label className="">Select Duration<sup className="text-danger">*</sup></label>
                                    <select
                                        className="form-select"
                                        value={filters.flag}
                                        onChange={(e) => handleFilterChange('flag', e.target.value)}
                                        disabled={isFilterLoading}
                                    >
                                        <option value="Day">Daily</option>
                                        {isWeekSelectionAvailable(filters.startDate, filters.endDate) && (
                                            <option value="Week">Weekly</option>
                                        )}
                                        {isMonthSelectionAvailable(filters.startDate, filters.endDate) && (
                                            <option value="Month">Monthly</option>
                                        )}
                                    </select>
                                </div>

                                <div className="form-group position-relative w-50">
                                <label className="">Select Type<sup className="text-danger">*</sup></label>
                                    <select
                                        className="form-select"
                                        value={chartType}
                                        onChange={(e) => setChartType(e.target.value)}
                                        disabled={isFilterLoading}
                                    >
                                        <option value="bar">Bar Chart</option>
                                        <option value="line">Line Chart</option>
                                    </select>
                                </div>
                            </div>

                            <div className="d-flex gap-2 dates align-items-center ps-md-2 mt-2 mt-md-0">
                                <div className="form-group position-relative w-50" style={{cursor: 'pointer'}}>
                                <label className="">Start Date<sup className="text-danger">*</sup></label>
                                    <i className="fas fa-calendar position-absolute" style={{ right: '10px', top: '68%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none' }}></i>
                                    <DatePicker
                                        selected={filters.startDate ? new Date(filters.startDate) : null}
                                        onChange={(date) => handleFilterChange('startDate', date ? date.toISOString().split('T')[0] : getMinAllowedDate())}
                                        className="form-control"
                                        dateFormat="yyyy-MM-dd"
                                        maxDate={new Date(filters.endDate)}
                                        minDate={new Date(getMinAllowedDate())}
                                        disabled={isFilterLoading || filters.flag === "Day"}
                                        placeholderText="Select start date"
                                        showMonthDropdown
                                        dropdownMode="select"
                                    />
                                </div>

                                <div>-</div>

                                <div className="form-group position-relative w-50" style={{cursor: 'pointer'}}>
                                <label className="">End Date<sup className="text-danger">*</sup></label>
                                    <i className="fas fa-calendar position-absolute" style={{ right: '10px', top: '68%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none' }}></i>
                                    <DatePicker
                                        selected={filters.endDate ? new Date(filters.endDate) : null}
                                        onChange={(date) => handleFilterChange('endDate', date ? date.toISOString().split('T')[0] : '')}
                                        className="form-control"
                                        dateFormat="yyyy-MM-dd"
                                        minDate={filters.startDate ? new Date(filters.startDate) : new Date(getMinAllowedDate())}
                                        maxDate={new Date()}
                                        disabled={isFilterLoading || filters.flag === "Day"}
                                        placeholderText="Select end date"
                                        showMonthDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="charts-container mb-3">
                <div className="row m-0">
                    <div className="col-md-6 ps-0 pe-md-2 pe-0">
                        <div className="chart-card">
                            <h3>Revenue Analysis</h3>
                            <div className="chart-wrapper" style={{ position: 'relative', height: '400px', width: '100%' }}>
                                {isFilterLoading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : chartData ? (
                                    <ChartComponent
                                        data={chartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'top'
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true
                                                }
                                            }
                                        }}
                                        style={{ height: '400px', width: '100%' }}
                                    />
                                ) : (
                                    <div className="text-center py-4">No data available</div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 ps-md-2 pe-0 ps-0 mt-3 mt-md-0">
                        <div className="chart-card">
                            <h3>Subscriber Analysis</h3>
                            <div className="chart-wrapper" style={{ position: 'relative', height: '400px', width: '100%' }}>
                                {isFilterLoading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : subscriberData ? (
                                    <ChartComponent
                                        data={subscriberData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'top'
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true
                                                }
                                            }
                                        }}
                                        style={{ height: '400px', width: '100%' }}
                                    />
                                ) : (
                                    <div className="text-center py-4">No data available</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
        </>
    );
};

export default BarGraph;